import React, { Component } from 'react';
import { Modal } from 'react-bootstrap';
import { HTTPService } from "../../../services/httpService";
import ServiceCard from './ServiceCard';
import TimeClockHeader from './TimeClockHeader';
import MaterialsCollectionModal from '../../partial/MaterialsCollectionModal';
import EnforcementModal from '../../common/EnforcementModal';
import { startService, finishService, updateRequestState, updateRequestOfferState, addAuditRecord, sendSmsNotification } from '../../../util/serviceActions';
import { checkIn } from '../../../services/timeTrackingService';
import { getCurrentPosition, GeolocationError } from '../../../util/geoUtils';
import moment from 'moment';

const API = new HTTPService();

/**
 * MyWork - Simplified work view for technicians
 *
 * Shows only services assigned to the current technician, grouped by date.
 * Technicians can start/finish their assigned work and track progress.
 *
 * Props (from App.js):
 * - user: Current user object
 * - typeId: Company/installer ID
 * - companyName: Company name
 */
class MyWork extends Component {
  state = {
    // Data
    assignedServices: [],
    loading: true,
    error: null,

    // UI State
    expandedServiceId: null, // Which service card is expanded (request_id:service_id)

    // Materials Collection
    categoryConfigs: {},
    showMaterialsModal: false,
    serviceToFinish: null, // Service object being finished
    requestForFinish: null, // Request context for finish

    // Materials View Modal
    showMaterialsViewModal: false,
    serviceForMaterialsView: null, // Service whose materials are being viewed

    // Technician Notes Modal
    showNotesModal: false,
    serviceForNotes: null, // Service being edited
    notesText: '',
    savingNotes: false,

    // Action state
    actionInProgress: null, // 'start' | 'finish' | null

    // Time Tracking
    isCheckedIn: false,
    currentSession: null,
    timeTrackingEnabled: false, // Whether time tracking feature is enabled for this location
    timeTrackingRequired: false, // Whether check-in is required before starting work
    locationId: null,

    // Enforcement Modal
    showEnforcementModal: false,
    pendingAction: null, // Service waiting for check-in before action
    enforcementCheckInLoading: false
  };

  async componentDidMount() {
    await this.loadMyWork();
    await this.loadCategoryConfigs();
    await this.loadLocationSettings();
  }

  loadMyWork = async () => {
    try {
      this.setState({ loading: true, error: null });

      const { user } = this.props;
      if (!user?.User_id) {
        this.setState({ error: 'User not loaded', loading: false });
        return;
      }

      // Fetch services assigned to this technician
      const { data: services } = await API.get(`/requestService/technician/${user.User_id}`);

      this.setState({
        assignedServices: services || [],
        loading: false
      });
    } catch (error) {
      console.error('Error loading my work:', error);
      this.setState({
        error: 'Failed to load your assigned work',
        loading: false
      });
    }
  };

  loadCategoryConfigs = async () => {
    try {
      const { data: configs } = await API.get('/serviceCategoryConfig');
      const categoryConfigs = {};
      if (Array.isArray(configs)) {
        configs.forEach(config => {
          categoryConfigs[config.category_name] = config;
        });
      }
      this.setState({ categoryConfigs });
    } catch (error) {
      console.error("Error loading category configs:", error);
    }
  };

  // Load location/time tracking settings from company data
  loadLocationSettings = async () => {
    try {
      const { typeId } = this.props;
      const companyData = JSON.parse(localStorage.getItem('companyAPI') || '{}');

      // Time tracking mode: 'none' | 'voluntary' | 'required'
      // - none: feature disabled, hide UI
      // - voluntary: feature enabled, check-in optional
      // - required: feature enabled, must check-in before working
      const timeTrackingMode = companyData.time_tracking_mode || 'none';
      const timeTrackingEnabled = timeTrackingMode !== 'none';
      const timeTrackingRequired = timeTrackingMode === 'required';

      // For now, location maps 1:1 with installer
      this.setState({
        locationId: typeId,
        timeTrackingEnabled,
        timeTrackingRequired
      });
    } catch (error) {
      console.error("Error loading location settings:", error);
    }
  };

  // Handle time tracking status change from TimeClockHeader
  handleTimeTrackingStatusChange = (isCheckedIn, session) => {
    this.setState({
      isCheckedIn,
      currentSession: session
    });

    // If check-in was successful and we have a pending action, execute it
    if (isCheckedIn && this.state.pendingAction) {
      const service = this.state.pendingAction;
      this.setState({ pendingAction: null, showEnforcementModal: false });
      this.handleStartService(service);
    }
  };

  // Handle enforcement modal check-in
  handleEnforcementCheckIn = async () => {
    const { locationId } = this.state;
    const { user } = this.props;

    try {
      this.setState({ enforcementCheckInLoading: true });

      // Get GPS position
      let gpsData = { lat: null, lng: null, accuracy_m: null };
      try {
        gpsData = await getCurrentPosition({ timeout: 15000 });
      } catch (gpsErr) {
        if (gpsErr instanceof GeolocationError && gpsErr.code === 'PERMISSION_DENIED') {
          alert('Location access is required to check in. Please enable location services.');
          this.setState({ enforcementCheckInLoading: false });
          return;
        }
      }

      // Attempt check-in
      const result = await checkIn({
        location_id: locationId,
        user_id: user.User_id,
        lat: gpsData.lat,
        lng: gpsData.lng,
        accuracy_m: gpsData.accuracy_m,
        device_info: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          timestamp: new Date().toISOString()
        }
      });

      if (result.success) {
        // Update state and proceed with pending action
        const pendingService = this.state.pendingAction;
        this.setState({
          isCheckedIn: true,
          currentSession: result.session,
          enforcementCheckInLoading: false,
          showEnforcementModal: false,
          pendingAction: null
        });

        // Execute the pending action
        if (pendingService) {
          this.handleStartService(pendingService);
        }
      } else {
        // Handle blocked check-in
        let errorMsg = result.message || 'Check-in failed';
        if (result.reason_code === 'outside_radius') {
          errorMsg = `You are ${result.distance_ft || '?'} feet from the shop. Please move within ${result.required_radius_ft || '?'} feet to check in.`;
        } else if (result.reason_code === 'accuracy_too_low') {
          errorMsg = `GPS accuracy is ${result.accuracy_m || '?'}m. Please wait for better signal.`;
        }
        alert(errorMsg);
        this.setState({ enforcementCheckInLoading: false });
      }
    } catch (err) {
      console.error('Enforcement check-in error:', err);
      const errorData = err.response?.data;
      alert(errorData?.message || 'Check-in failed. Please try again.');
      this.setState({ enforcementCheckInLoading: false });
    }
  };

  // Close enforcement modal
  handleCloseEnforcementModal = () => {
    this.setState({
      showEnforcementModal: false,
      pendingAction: null
    });
  };

  // Toggle expanded service card
  handleToggleExpand = (requestId, serviceId) => {
    const cardId = `${requestId}:${serviceId}`;
    this.setState(prevState => ({
      expandedServiceId: prevState.expandedServiceId === cardId ? null : cardId
    }));
  };

  // Show materials used for a service
  handleShowMaterials = (service) => {
    this.setState({
      showMaterialsViewModal: true,
      serviceForMaterialsView: service
    });
  };

  // Hide materials view modal
  handleHideMaterialsView = () => {
    this.setState({
      showMaterialsViewModal: false,
      serviceForMaterialsView: null
    });
  };

  // Start a service
  handleStartService = async (service) => {
    console.log('🚀 handleStartService called with:', service);

    // Check if time tracking enforcement is required
    const { timeTrackingRequired, isCheckedIn } = this.state;
    if (timeTrackingRequired && !isCheckedIn) {
      // Show enforcement modal and store pending action
      this.setState({
        showEnforcementModal: true,
        pendingAction: service
      });
      return;
    }

    try {
      this.setState({ actionInProgress: 'start' });

      const { user, typeId } = this.props;
      console.log('🔧 Calling startService with:', {
        requestId: service.request_id,
        serviceId: service.service_id,
        installerId: parseInt(typeId),
        userId: user.User_id,
        userType: 'installer'
      });

      await startService({
        requestId: service.request_id,
        serviceId: service.service_id,
        installerId: parseInt(typeId),
        userId: user.User_id,
        userType: 'installer'
      });

      console.log('✅ startService completed successfully');

      // If this is the first service being started on the job (request is in "received" state),
      // update the request state to "started"
      if (service.current_state === 'received') {
        await updateRequestState({
          requestId: service.request_id,
          currentState: 'started'
        });

        await updateRequestOfferState({
          requestId: service.request_id,
          installerId: parseInt(typeId),
          currentStatus: 'started'
        });

        // Add audit record for the job state change
        await addAuditRecord({
          requestId: service.request_id,
          status: 'started',
          serviceId: null,
          userId: user.User_id,
          userType: 'installer'
        });

        // Send SMS notification to customer/dealer contact (non-blocking)
        const { companyName } = this.props;
        try {
          if (service.request_type === 'dealer') {
            // For dealer jobs, notify the dealer contact person
            const contactId = service.dealer_contact || service.dealerContact || service.requestor;
            await sendSmsNotification({
              messageType: 'User',
              userType: 'dealer',
              recipientId: contactId,
              text: `${companyName} has started work on your vehicle`
            });
          } else {
            // For retail/customer jobs, notify the customer on record
            await sendSmsNotification({
              messageType: 'Customer',
              userType: 'customer',
              recipientId: service.requestor,
              text: `${companyName} has started work on your vehicle`
            });
          }
        } catch (smsError) {
          console.warn('Failed to send SMS notification:', smsError);
        }
      }

      // Update local state
      this.setState(prevState => ({
        assignedServices: prevState.assignedServices.map(s =>
          (s.request_id === service.request_id && s.service_id === service.service_id)
            ? { ...s, service_state: 'started', started_at: new Date().toISOString(), started_by: user.User_id, current_state: 'started' }
            : s
        ),
        actionInProgress: null
      }));

      alert('Service started!');
    } catch (error) {
      console.error('Error starting service:', error);
      alert('Error starting service. Please try again.');
      this.setState({ actionInProgress: null });
    }
  };

  // Finish a service - check if materials collection needed
  handleFinishService = async (service) => {
    const { categoryConfigs } = this.state;

    // Check if this category needs data collection
    const categoryConfig = categoryConfigs[service.service_category];

    // Check shop-level materials tracking setting
    const companyData = JSON.parse(localStorage.getItem('companyAPI') || '{}');
    const materialsTrackingEnabled = companyData?.materials_tracking_enabled !== false;

    if (materialsTrackingEnabled && categoryConfig?.data_collect) {
      // Show materials collection modal
      this.setState({
        showMaterialsModal: true,
        serviceToFinish: service
      });
    } else {
      // No materials needed, complete directly
      await this.completeFinishService(service, null);
    }
  };

  // Complete the finish action (called after materials collection or directly)
  completeFinishService = async (service, materialsData) => {
    try {
      this.setState({ actionInProgress: 'finish', showMaterialsModal: false });

      const { user, typeId } = this.props;

      await finishService({
        requestId: service.request_id,
        serviceId: service.service_id,
        installerId: parseInt(typeId),
        userId: user.User_id,
        userType: 'installer',
        materialsData
      });

      // Update local state
      this.setState(prevState => ({
        assignedServices: prevState.assignedServices.map(s =>
          (s.request_id === service.request_id && s.service_id === service.service_id)
            ? { ...s, service_state: 'finished', finished_at: new Date().toISOString(), finished_by: user.User_id }
            : s
        ),
        actionInProgress: null,
        serviceToFinish: null
      }));

      alert('Service completed!');
    } catch (error) {
      console.error('Error finishing service:', error);
      alert('Error completing service. Please try again.');
      this.setState({ actionInProgress: null, serviceToFinish: null });
    }
  };

  // Handle materials modal submission
  handleMaterialsSubmit = (materialsDataArray) => {
    const { serviceToFinish } = this.state;
    if (serviceToFinish) {
      // materialsDataArray is an array, but we only have one service
      this.completeFinishService(serviceToFinish, materialsDataArray[0]);
    }
  };

  // Handle materials modal close
  handleMaterialsModalHide = () => {
    this.setState({
      showMaterialsModal: false,
      serviceToFinish: null
    });
  };

  // =========================================================================
  // Technician Notes Handlers
  // =========================================================================

  // Show notes modal
  handleShowNotes = (service) => {
    this.setState({
      showNotesModal: true,
      serviceForNotes: service,
      notesText: service.technician_instructions || ''
    });
  };

  // Hide notes modal
  handleHideNotes = () => {
    this.setState({
      showNotesModal: false,
      serviceForNotes: null,
      notesText: ''
    });
  };

  // Handle notes text change
  handleNotesChange = (text) => {
    this.setState({ notesText: text });
  };

  // Save notes
  handleSaveNotes = async () => {
    const { serviceForNotes, notesText } = this.state;
    if (!serviceForNotes) return;

    try {
      this.setState({ savingNotes: true });

      // Save to backend
      const patchUrl = serviceForNotes.id
        ? `/requestService/row/${serviceForNotes.id}`
        : `/requestService/${serviceForNotes.request_id}/${serviceForNotes.service_id}`;
      await API.patch(patchUrl, {
        technician_instructions: notesText
      });

      // Update local state
      this.setState(prevState => ({
        assignedServices: prevState.assignedServices.map(s =>
          (serviceForNotes.id ? s.id === serviceForNotes.id : (s.request_id === serviceForNotes.request_id && s.service_id === serviceForNotes.service_id))
            ? { ...s, technician_instructions: notesText }
            : s
        ),
        showNotesModal: false,
        serviceForNotes: null,
        notesText: '',
        savingNotes: false
      }));

    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Error saving notes. Please try again.');
      this.setState({ savingNotes: false });
    }
  };

  // Group services by work date, then by state within each date
  // Structure: Past Due at top, then Today, Tomorrow, future dates, then Unscheduled
  // Within each date: Scheduled, In Progress, Completed
  groupServicesByDate = (services) => {
    const today = moment().startOf('day');
    const groups = {};

    services.forEach(service => {
      let dateKey;

      if (!service.work_date) {
        dateKey = 'unscheduled';
      } else {
        const workDate = moment(service.work_date).startOf('day');
        // Check if past due (before today and not finished)
        if (workDate.isBefore(today) && service.service_state !== 'finished') {
          dateKey = 'pastdue';
        } else {
          dateKey = workDate.format('YYYY-MM-DD');
        }
      }

      if (!groups[dateKey]) {
        groups[dateKey] = {
          scheduled: [],
          inProgress: [],
          completed: []
        };
      }

      // Sort into state buckets
      if (service.service_state === 'finished') {
        groups[dateKey].completed.push(service);
      } else if (service.service_state === 'started') {
        groups[dateKey].inProgress.push(service);
      } else {
        groups[dateKey].scheduled.push(service);
      }
    });

    // Sort dates: pastdue first, then today, then future dates, then unscheduled
    const todayStr = today.format('YYYY-MM-DD');
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === 'pastdue') return -1;
      if (b === 'pastdue') return 1;
      if (a === 'unscheduled') return 1;
      if (b === 'unscheduled') return -1;
      if (a === todayStr) return -1;
      if (b === todayStr) return 1;
      return moment(a).diff(moment(b));
    });

    return sortedKeys.map(dateKey => ({
      date: dateKey,
      scheduled: groups[dateKey].scheduled,
      inProgress: groups[dateKey].inProgress,
      completed: groups[dateKey].completed,
      totalActive: groups[dateKey].scheduled.length + groups[dateKey].inProgress.length
    }));
  };

  // Format date for display
  formatDateHeader = (dateKey) => {
    if (dateKey === 'unscheduled') {
      return 'Unscheduled';
    }
    if (dateKey === 'pastdue') {
      return 'Past Due';
    }

    const date = moment(dateKey);
    const today = moment().startOf('day');
    const tomorrow = moment().add(1, 'day').startOf('day');

    if (date.isSame(today, 'day')) {
      return 'Today';
    }
    if (date.isSame(tomorrow, 'day')) {
      return 'Tomorrow';
    }
    return date.format('dddd, MMMM D');
  };

  // Get summary stats
  getStats = () => {
    const { assignedServices } = this.state;
    const total = assignedServices.length;
    const notStarted = assignedServices.filter(s => s.service_state === 'new' || s.service_state === 'booked').length;
    const inProgress = assignedServices.filter(s => s.service_state === 'started').length;
    const completed = assignedServices.filter(s => s.service_state === 'finished').length;

    return { total, notStarted, inProgress, completed };
  };

  render() {
    const {
      assignedServices,
      loading,
      error,
      expandedServiceId,
      categoryConfigs,
      showMaterialsModal,
      serviceToFinish,
      actionInProgress,
      showMaterialsViewModal,
      serviceForMaterialsView,
      showNotesModal,
      serviceForNotes,
      notesText,
      savingNotes,
      // Time tracking state
      isCheckedIn,
      timeTrackingEnabled,
      timeTrackingRequired,
      locationId,
      showEnforcementModal,
      enforcementCheckInLoading
    } = this.state;

    const { user, typeId } = this.props;
    const stats = this.getStats();
    const groupedServices = this.groupServicesByDate(assignedServices);

    return (
      <div className="container-fluid mt-4">
        {/* Time Clock Header - Check In/Out (only shown if time tracking is enabled) */}
        {timeTrackingEnabled && locationId && user?.User_id && (
          <TimeClockHeader
            locationId={locationId}
            userId={user.User_id}
            onStatusChange={this.handleTimeTrackingStatusChange}
          />
        )}

        {/* Header */}
        <div className="row mb-4">
          <div className="col">
            <h4>My Work</h4>
            <p className="text-muted mb-0">
              {stats.total === 0 ? (
                'No services assigned to you'
              ) : (
                <>
                  {stats.notStarted > 0 && `${stats.notStarted} to do`}
                  {stats.notStarted > 0 && stats.inProgress > 0 && ' | '}
                  {stats.inProgress > 0 && `${stats.inProgress} in progress`}
                  {(stats.notStarted > 0 || stats.inProgress > 0) && stats.completed > 0 && ' | '}
                  {stats.completed > 0 && `${stats.completed} completed`}
                </>
              )}
            </p>
          </div>
          <div className="col-auto">
            <button
              className="btn btn-outline-secondary"
              onClick={this.loadMyWork}
              disabled={loading}
            >
              <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''}`}></i>
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="alert alert-danger">
            {error}
            <button className="btn btn-link" onClick={this.loadMyWork}>
              Retry
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading your work...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && assignedServices.length === 0 && (
          <div className="text-center py-5">
            <i className="fa fa-check-circle fa-3x text-success mb-3"></i>
            <h5>All caught up!</h5>
            <p className="text-muted">
              You don't have any services assigned to you right now.
            </p>
          </div>
        )}

        {/* Service List by Date */}
        {!loading && !error && assignedServices.length > 0 && (
          <div className="services-list">
            {groupedServices.map(group => {
              const totalServices = group.scheduled.length + group.inProgress.length + group.completed.length;
              const isPastDue = group.date === 'pastdue';

              return (
                <div key={group.date} className="mb-4">
                  {/* Date Header */}
                  <h5 className={`text-uppercase mb-3 border-bottom pb-2 ${isPastDue ? 'text-danger' : 'text-muted'}`}>
                    {isPastDue && <i className="fa fa-exclamation-triangle mr-2"></i>}
                    {this.formatDateHeader(group.date)}
                    <span className={`badge ml-2 ${isPastDue ? 'badge-danger' : 'badge-secondary'}`}>
                      {totalServices}
                    </span>
                  </h5>

                  {/* Scheduled Section */}
                  {group.scheduled.length > 0 && (
                    <div className="mb-3">
                      <h6 className="text-muted small mb-2">
                        <i className="fa fa-clock-o mr-1"></i>
                        Scheduled ({group.scheduled.length})
                      </h6>
                      {group.scheduled.map(service => {
                        const cardId = `${service.request_id}:${service.service_id}`;
                        return (
                          <ServiceCard
                            key={cardId}
                            service={service}
                            expanded={expandedServiceId === cardId}
                            onToggle={() => this.handleToggleExpand(service.request_id, service.service_id)}
                            onStart={() => this.handleStartService(service)}
                            onFinish={() => this.handleFinishService(service)}
                            actionInProgress={actionInProgress}
                            onShowMaterials={this.handleShowMaterials}
                            onAddNotes={this.handleShowNotes}
                            timeTrackingRequired={timeTrackingRequired}
                            isCheckedIn={isCheckedIn}
                          />
                        );
                      })}
                    </div>
                  )}

                  {/* In Progress Section */}
                  {group.inProgress.length > 0 && (
                    <div className="mb-3">
                      <h6 className="text-primary small mb-2">
                        <i className="fa fa-spinner mr-1"></i>
                        In Progress ({group.inProgress.length})
                      </h6>
                      {group.inProgress.map(service => {
                        const cardId = `${service.request_id}:${service.service_id}`;
                        return (
                          <ServiceCard
                            key={cardId}
                            service={service}
                            expanded={expandedServiceId === cardId}
                            onToggle={() => this.handleToggleExpand(service.request_id, service.service_id)}
                            onStart={() => this.handleStartService(service)}
                            onFinish={() => this.handleFinishService(service)}
                            actionInProgress={actionInProgress}
                            onShowMaterials={this.handleShowMaterials}
                            onAddNotes={this.handleShowNotes}
                            timeTrackingRequired={timeTrackingRequired}
                            isCheckedIn={isCheckedIn}
                          />
                        );
                      })}
                    </div>
                  )}

                  {/* Completed Section */}
                  {group.completed.length > 0 && (
                    <div className="mb-3">
                      <h6 className="text-success small mb-2">
                        <i className="fa fa-check mr-1"></i>
                        Completed ({group.completed.length})
                      </h6>
                      {group.completed.map(service => {
                        const cardId = `${service.request_id}:${service.service_id}`;
                        return (
                          <ServiceCard
                            key={cardId}
                            service={service}
                            expanded={expandedServiceId === cardId}
                            onToggle={() => this.handleToggleExpand(service.request_id, service.service_id)}
                            onStart={() => this.handleStartService(service)}
                            onFinish={() => this.handleFinishService(service)}
                            actionInProgress={actionInProgress}
                            onShowMaterials={this.handleShowMaterials}
                            onAddNotes={this.handleShowNotes}
                            timeTrackingRequired={timeTrackingRequired}
                            isCheckedIn={isCheckedIn}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Materials Collection Modal */}
        {serviceToFinish && (
          <MaterialsCollectionModal
            show={showMaterialsModal}
            onHide={this.handleMaterialsModalHide}
            services={[serviceToFinish]}
            categoryConfigs={categoryConfigs}
            installerId={typeId}
            onSubmit={this.handleMaterialsSubmit}
          />
        )}

        {/* Technician Notes Modal */}
        <Modal
          show={showNotesModal}
          onHide={this.handleHideNotes}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fa fa-sticky-note mr-2"></i>
              Technician Notes
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {serviceForNotes && (
              <div>
                <div className="mb-3">
                  <label className="font-weight-bold">
                    Service: {serviceForNotes.service_category}
                    {serviceForNotes.custom_description && ` - ${serviceForNotes.custom_description}`}
                  </label>
                  {/* Show recommended product if available */}
                  {serviceForNotes.recommended_product_id && serviceForNotes.recommended_product_name && (
                    <div className="text-success mb-2">
                      <i className="fa fa-check mr-1"></i>
                      <strong>Recommended Product:</strong>{' '}
                      {serviceForNotes.recommended_product_brand
                        ? `${serviceForNotes.recommended_product_brand} - ${serviceForNotes.recommended_product_name}`
                        : serviceForNotes.recommended_product_name}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Notes / Instructions</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={notesText}
                    onChange={(e) => this.handleNotesChange(e.target.value)}
                    placeholder="Add notes or instructions for this service..."
                  />
                  <small className="text-muted">
                    Add any notes about the service, materials used, issues encountered, etc.
                  </small>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-secondary"
              onClick={this.handleHideNotes}
              disabled={savingNotes}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={this.handleSaveNotes}
              disabled={savingNotes}
            >
              {savingNotes ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fa fa-save mr-2"></i>
                  Save Notes
                </>
              )}
            </button>
          </Modal.Footer>
        </Modal>

        {/* Materials View Modal - Shows collected materials for a service */}
        <Modal
          show={showMaterialsViewModal}
          onHide={this.handleHideMaterialsView}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fa fa-cubes mr-2"></i>
              Materials Used
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {serviceForMaterialsView && (() => {
              // Parse materials_data if it exists
              let parsedMaterialsData = null;
              if (serviceForMaterialsView.materials_data) {
                try {
                  parsedMaterialsData = typeof serviceForMaterialsView.materials_data === 'string'
                    ? JSON.parse(serviceForMaterialsView.materials_data)
                    : serviceForMaterialsView.materials_data;
                } catch (e) {
                  console.error('Error parsing materials_data:', e);
                }
              }

              return (
                <div>
                  <div className="mb-3">
                    <strong>{serviceForMaterialsView.service_category}</strong>
                    {serviceForMaterialsView.custom_description && (
                      <span className="text-muted"> - {serviceForMaterialsView.custom_description}</span>
                    )}
                  </div>

                  {/* Display materials array */}
                  {serviceForMaterialsView.materials && serviceForMaterialsView.materials.length > 0 ? (
                    <div className="materials-list">
                      {serviceForMaterialsView.materials.map((material, index) => (
                        <div key={index} className="material-item p-2 mb-2 border rounded">
                          <div className="font-weight-bold">
                            {material.brand && <span>{material.brand} - </span>}
                            {material.product_name}
                          </div>
                          {material.quantity_used && (
                            <div className="text-muted small">
                              Quantity: {material.quantity_used} {material.unit_code || ''}
                            </div>
                          )}
                          {material.barcode && (
                            <div className="text-muted small">
                              Roll: <code>{material.barcode}</code>
                            </div>
                          )}
                          {material.lot_number && (
                            <div className="text-muted small">
                              Lot: <code>{material.lot_number}</code>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : parsedMaterialsData ? (
                    /* Display from materials_data */
                    <div className="material-item p-2 border rounded">
                      <div className="font-weight-bold">
                        {parsedMaterialsData.product_brand && <span>{parsedMaterialsData.product_brand} - </span>}
                        {parsedMaterialsData.product_name}
                      </div>
                      {parsedMaterialsData.roll_number && (
                        <div className="text-muted small">
                          Roll: <code>{parsedMaterialsData.roll_number}</code>
                        </div>
                      )}
                      {parsedMaterialsData.lot_number && (
                        <div className="text-muted small">
                          Lot: <code>{parsedMaterialsData.lot_number}</code>
                        </div>
                      )}
                      {parsedMaterialsData.inventory_items && parsedMaterialsData.inventory_items.length > 0 && (
                        <div className="text-muted small mt-2">
                          {parsedMaterialsData.inventory_items.map((item, idx) => (
                            <div key={idx}>Quantity used: {item.quantity_used}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : serviceForMaterialsView.material_brand && serviceForMaterialsView.material_product ? (
                    /* Fallback to material_brand/material_product fields */
                    <div className="material-item p-2 border rounded">
                      <div className="font-weight-bold">
                        {serviceForMaterialsView.material_brand} - {serviceForMaterialsView.material_product}
                      </div>
                      {serviceForMaterialsView.material_roll_number && (
                        <div className="text-muted small">
                          Roll: <code>{serviceForMaterialsView.material_roll_number}</code>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-muted">No materials information available.</p>
                  )}
                </div>
              );
            })()}
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-secondary" onClick={this.handleHideMaterialsView}>
              Close
            </button>
          </Modal.Footer>
        </Modal>

        {/* Time Tracking Enforcement Modal */}
        <EnforcementModal
          isOpen={showEnforcementModal}
          onClose={this.handleCloseEnforcementModal}
          onCheckIn={this.handleEnforcementCheckIn}
          loading={enforcementCheckInLoading}
        />
      </div>
    );
  }
}

export default MyWork;
